import { createContext, useEffect, useState } from "react";

interface JobTitleOption {
  id: number;
  name: string;
}

interface JobFunctionOption {
  id: number;
  name: string;
}

interface LocationOption {
  id: number;
  name: string;
}

interface IndustryOption {
  id: number;
  name: string;
}

// Define the interface for the context
interface VonqApiContextProps {
  selectedValues: {
    jobTitle: JobTitleOption | null; 
    jobTitleSearchTerm: string; 
    jobFunction: JobFunctionOption | null;
    location: LocationOption | null;
    industries: IndustryOption[];
  };
  setSelectedValues: (key: string, value: any) => void;
  setJobTitle: (value: JobTitleOption | null) => void;
  setJobTitleSearchTerm: (value: string) => void;
  setJobFunction: (value: JobFunctionOption | null) => void;
  setLocation: (value: LocationOption | null) => void;
  setIndustries: (value: IndustryOption[]) => void;
}

export const ApiContext = createContext<VonqApiContextProps | undefined>(undefined);

export const VonqApiProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedValues, setSelectedValues] = useState({
    jobTitle: null as JobTitleOption | null,
    jobTitleSearchTerm: "", // For the search input
    jobFunction: null as JobFunctionOption | null,
    location: null as LocationOption | null,
    industries: [] as IndustryOption[],
  });

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("campaignData");

    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setSelectedValues(parsedData);
      } catch (error) {
        localStorage.removeItem("campaignData");
      }
    }
  }, []);

  // Save the selected values to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem("campaignData", JSON.stringify(selectedValues));
    } catch (error) {
    }
  }, [selectedValues]);

  // Generic function to update a specific key in the selectedValues object
  const updateSelectedValues = (key: string, value: any) => {
    setSelectedValues((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  // Specific setters for better type safety and easier usage
  const setJobTitle = (value: JobTitleOption | null) => {
    setSelectedValues((prevState) => ({
      ...prevState,
      jobTitle: value,
    }));
  };

  const setJobTitleSearchTerm = (value: string) => {
    setSelectedValues((prevState) => ({
      ...prevState,
      jobTitleSearchTerm: value,
    }));
  };

  const setJobFunction = (value: JobFunctionOption | null) => {
    setSelectedValues((prevState) => ({
      ...prevState,
      jobFunction: value,
    }));
  };

  const setLocation = (value: LocationOption | null) => {
    setSelectedValues((prevState) => ({
      ...prevState,
      location: value,
    }));
  };

  const setIndustries = (value: IndustryOption[]) => {
    setSelectedValues((prevState) => ({
      ...prevState,
      industries: value,
    }));
  };

  return (
    <ApiContext.Provider
      value={{
        selectedValues,
        setSelectedValues: updateSelectedValues,
        setJobTitle,
        setJobTitleSearchTerm,
        setJobFunction,
        setLocation,
        setIndustries,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};